import { Component, ErrorInfo, ReactNode } from "react";
import { IconAlertTriangle, IconRefresh } from "@tabler/icons-react";
import { Button } from "../ui/button";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";

// Define process for Node.js environment variables
declare const process: {
  env: {
    NODE_ENV: string;
  };
};

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({
      error,
      errorInfo,
    });

    // Log error to console in development
    if (process.env.NODE_ENV === "development") {
      console.error("ErrorBoundary caught an error:", error, errorInfo);
    }

    // Here you could also log to an error reporting service
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="max-w-md mx-auto py-12">
          <div className="flex flex-col items-center space-y-4">
            <IconAlertTriangle size={64} className="text-destructive" />
            <h2 className="text-2xl font-bold text-center">
              Something went wrong
            </h2>
            <p className="text-center text-muted-foreground">
              An unexpected error occurred. Please try refreshing the page or
              contact support if the problem persists.
            </p>

            {process.env.NODE_ENV === "development" && this.state.error && (
              <Alert className="w-full">
                <IconAlertTriangle className="h-4 w-4" />
                <AlertTitle>Error Details (Development Mode)</AlertTitle>
                <AlertDescription>
                  <div className="text-sm font-mono">
                    {this.state.error.message}
                  </div>
                  {this.state.errorInfo && (
                    <div className="text-xs font-mono mt-2">
                      {this.state.errorInfo.componentStack}
                    </div>
                  )}
                </AlertDescription>
              </Alert>
            )}

            <div className="flex gap-2">
              <Button onClick={this.handleReset} variant="outline">
                <IconRefresh size={16} className="mr-2" />
                Try Again
              </Button>
              <Button onClick={() => window.location.reload()}>
                Reload Page
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
